import axios from 'axios';

// On production domain (kahveligi.com), always use same-origin to avoid cross-domain issues
const hostname = window.location.hostname;
const isProduction = hostname === 'kahveligi.com' || hostname === 'www.kahveligi.com';
const BACKEND_URL = isProduction ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || window.location.origin);
const API = `${BACKEND_URL}/api`;

const axiosInstance = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Add token to every request for iOS Safari compatibility
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 errors - clear token and redirect to login
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      // Don't redirect if already on login/register page
      if (!window.location.pathname.includes('/login') && 
          !window.location.pathname.includes('/register') &&
          !window.location.pathname.includes('/auth')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const api = {
  // Auth
  register: (data) => axiosInstance.post('/auth/register', data),
  login: (data) => axiosInstance.post('/auth/login', data),
  processGoogleSession: (sessionId) => axiosInstance.post('/auth/session', { session_id: sessionId }),
  getMe: () => axiosInstance.get('/auth/me'),
  logout: () => axiosInstance.post('/auth/logout'),
  
  // Password Reset
  requestPasswordReset: (email) => axiosInstance.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => axiosInstance.post('/auth/reset-password', { token, new_password: newPassword }),
  verifyResetToken: (token) => axiosInstance.get(`/auth/verify-reset-token/${token}`),
  
  // Onboarding
  completeOnboarding: () => axiosInstance.post('/auth/complete-onboarding'),
  
  // Users
  getUser: (username) => axiosInstance.get(`/users/${username}`),
  updateProfile: (data) => axiosInstance.put('/users/profile', data),
  uploadProfilePicture: (imageData) => axiosInstance.post('/users/profile/picture', { image: imageData }),
  deleteProfilePicture: () => axiosInstance.delete('/users/profile/picture'),
  searchUsers: (query) => axiosInstance.get(`/users/search/${query}`),
  changePassword: (data) => axiosInstance.post('/users/change-password', data),
  changeEmail: (data) => axiosInstance.post('/users/change-email', data),
  changeUsername: (data) => axiosInstance.post('/users/change-username', data),
  checkUsernameAvailability: (username) => axiosInstance.get(`/users/check-username/${username}`),
  getUsernameChangesRemaining: () => axiosInstance.get('/users/username-changes-remaining'),
  
  // Global Search
  search: (queryString) => axiosInstance.get(`/search?${queryString}`),
  
  // Follow
  follow: (userId) => axiosInstance.post(`/follow/${userId}`),
  unfollow: (userId) => axiosInstance.delete(`/follow/${userId}`),
  followUser: (userId) => axiosInstance.post(`/follow/${userId}`),
  unfollowUser: (userId) => axiosInstance.delete(`/follow/${userId}`),
  checkFollowing: (userId) => axiosInstance.get(`/users/${userId}/is-following`),
  getFollowers: (userId) => axiosInstance.get(`/users/${userId}/followers`),
  getFollowing: (userId) => axiosInstance.get(`/users/${userId}/following`),
  
  // Games
  getGames: (category) => axiosInstance.get('/games', { params: category ? { category } : {} }),
  getGame: (gameId) => axiosInstance.get(`/games/${gameId}`),
  getGameCategories: () => axiosInstance.get('/games/categories'),
  createGame: (data) => axiosInstance.post('/games', data),
  
  // Leagues
  getLeagues: () => axiosInstance.get('/leagues'),
  getLeague: (leagueId) => axiosInstance.get(`/leagues/${leagueId}`),
  createLeague: (data) => axiosInstance.post('/leagues', data),
  getUserLeagues: (userId) => axiosInstance.get(`/leagues/user/${userId}`),
  checkLeagueAdmin: (leagueId) => axiosInstance.get(`/leagues/${leagueId}/is-admin`),
  getLeagueAdmins: (leagueId) => axiosInstance.get(`/leagues/${leagueId}/admins`),
  addLeagueAdmin: (leagueId, userId) => axiosInstance.post(`/leagues/${leagueId}/admins/${userId}`),
  removeLeagueAdmin: (leagueId, userId) => axiosInstance.delete(`/leagues/${leagueId}/admins/${userId}`),
  enableOnlineMode: (leagueId) => axiosInstance.patch(`/leagues/${leagueId}/enable-online`),
  
  // Weeks
  getLeagueWeeks: (leagueId) => axiosInstance.get(`/weeks/league/${leagueId}`),
  getWeek: (weekId) => axiosInstance.get(`/weeks/${weekId}`),
  createWeek: (data) => axiosInstance.post('/weeks', data),
  updateWeek: (weekId, data) => axiosInstance.put(`/weeks/${weekId}`, data),
  deleteWeek: (weekId) => axiosInstance.delete(`/weeks/${weekId}`),
  
  // AI
  generateCommentary: (weekId) => axiosInstance.post(`/ai/commentary/${weekId}`),
  
  // Tournaments
  getTournaments: () => axiosInstance.get('/tournaments'),
  getTournament: (tournamentId) => axiosInstance.get(`/tournaments/${tournamentId}`),
  createTournament: (data) => axiosInstance.post('/tournaments', data),
  updateMatchResult: (tournamentId, matchId, data) => axiosInstance.put(`/tournaments/${tournamentId}/matches/${matchId}`, data),
  
  // Custom Games
  createCustomGame: (data) => axiosInstance.post('/games/custom', data),
  getCustomGames: () => axiosInstance.get('/games/custom'),
  
  // Online Game Rooms
  createGameRoom: (leagueId, rules) => axiosInstance.post('/game/rooms', { league_id: leagueId, rules }),
  getGameRoom: (roomId) => axiosInstance.get(`/game/rooms/${roomId}`),
  getLeagueRooms: (leagueId) => axiosInstance.get(`/game/leagues/${leagueId}/rooms`),
  joinGameRoom: (roomId, seatIndex) => axiosInstance.post(`/game/rooms/${roomId}/join`, { seat_index: seatIndex }),
  leaveGameRoom: (roomId) => axiosInstance.post(`/game/rooms/${roomId}/leave`),
  setPlayerReady: (roomId, isReady) => axiosInstance.post(`/game/rooms/${roomId}/ready`, { is_ready: isReady }),
  startGame: (roomId) => axiosInstance.post(`/game/rooms/${roomId}/start`),
  gameAction: (roomId, action) => axiosInstance.post(`/game/rooms/${roomId}/action`, action),
  
  // Season Management
  resetLeagueSeason: (leagueId) => axiosInstance.post(`/leagues/${leagueId}/reset-season`),
  getLeagueSeasons: (leagueId) => axiosInstance.get(`/leagues/${leagueId}/seasons`),
  
  // Statistics
  getPlatformStatistics: () => axiosInstance.get('/statistics/platform'),
  getUserStatistics: (userId) => axiosInstance.get(`/statistics/user/${userId}`),
  
  // Badges
  getAllBadges: () => axiosInstance.get('/badges'),
  getUserBadges: (userId) => axiosInstance.get(`/users/${userId}/badges`),
  nominateFairPlay: (userId) => axiosInstance.post(`/users/${userId}/badges/fair_play/award`),
  
  // Push Notifications
  subscribeToPush: (subscription) => axiosInstance.post('/push/subscribe', subscription),
  unsubscribeFromPush: () => axiosInstance.post('/push/unsubscribe'),
  getPushStatus: () => axiosInstance.get('/push/status'),
  getNotificationSettings: () => axiosInstance.get('/notifications/settings'),
  updateNotificationSettings: (settings) => axiosInstance.put('/notifications/settings', settings),
  
  // Account Management
  deleteAccount: () => axiosInstance.delete('/account'),
  
  // Social Sharing
  getLeagueShareData: (leagueId) => axiosInstance.get(`/share/league/${leagueId}`),
  getTournamentShareData: (tournamentId) => axiosInstance.get(`/share/tournament/${tournamentId}`),
  getProfileShareData: (username) => axiosInstance.get(`/share/profile/${username}`),
  trackShare: (data) => axiosInstance.post('/share/track', data),
  
  // WhatsApp
  setWhatsAppConfig: (data) => axiosInstance.post('/users/whatsapp-config', data),
  getWhatsAppConfig: () => axiosInstance.get('/users/whatsapp-config'),
  sendLeagueWhatsAppSummary: (leagueId) => axiosInstance.post(`/leagues/${leagueId}/send-whatsapp-summary`),
  testWhatsApp: (data) => axiosInstance.post('/whatsapp/test', data),
  
  // Export
  exportLeagueData: (leagueId) => axiosInstance.get(`/leagues/${leagueId}/export/data`),
  exportTournamentData: (tournamentId) => axiosInstance.get(`/tournaments/${tournamentId}/export/data`),
  
  // Instagram Story Export
  exportWeekStory: (weekId) => axiosInstance.get(`/export/story/week/${weekId}`),
  exportLeagueStory: (leagueId) => axiosInstance.get(`/export/story/league/${leagueId}`),
  exportTournamentStory: (tournamentId) => axiosInstance.get(`/export/story/tournament/${tournamentId}`),
  
  // Messages
  getConversations: () => axiosInstance.get('/messages'),
  getMessages: (userId) => axiosInstance.get(`/messages/${userId}`),
  sendMessage: (data) => axiosInstance.post('/messages', data),
  
  // Notifications
  getNotifications: () => axiosInstance.get('/notifications'),
  markNotificationsRead: () => axiosInstance.post('/notifications/read'),
  getUnreadCount: () => axiosInstance.get('/notifications/unread-count'),
  getNotificationSettings: () => axiosInstance.get('/notifications/settings'),
  updateNotificationSettings: (data) => axiosInstance.put('/notifications/settings', data),
  
  // Statistics
  getUserStatistics: (userId) => axiosInstance.get(`/statistics/user/${userId}`),
  getUserDetailedStatistics: (userId) => axiosInstance.get(`/statistics/user/${userId}/detailed`),
  getPlatformStatistics: () => axiosInstance.get('/statistics/platform'),
  getLeagueStatistics: (leagueId) => axiosInstance.get(`/statistics/league/${leagueId}`),
  getLeagueAIAnalysis: (leagueId) => axiosInstance.get(`/statistics/league/${leagueId}/ai-analysis`),
  
  // Feed
  getFeed: () => axiosInstance.get('/feed'),
  
  // Championship Probability
  getChampionshipProbability: (leagueId) => axiosInstance.get(`/leagues/${leagueId}/championship-probability`),
  
  // Discover
  discoverUsers: () => axiosInstance.get('/discover/users'),
  discoverLeagues: () => axiosInstance.get('/discover/leagues'),
  
  // Legacy
  getStandings: () => axiosInstance.get('/standings'),
};

export default api;
